import type { CssVarsObject } from './../../ricos-types/index.js';

import { FALLBACK_COLOR_BRIGHT_DEFAULT, FALLBACK_COLOR_DEFAULT } from '../theme-constants.js';

function rgbaToHexA(rgbaArr: string[], withAlpha?: boolean) {
  const rgba = rgbaArr.map((r, index) => {
    if (r.indexOf('%') > -1) {
      const p = parseFloat(r.substr(0, r.length - 1)) / 100;

      if (index < 3) {
        return Math.round(p * 255);
      }
      return p;
    }
    return parseFloat(r);
  });

  let r = Number(rgba[0]).toString(16),
    g = Number(rgba[1]).toString(16),
    b = Number(rgba[2]).toString(16),
    a = Math.round(Number(rgba[3]) * 255).toString(16);

  if (r.length === 1) r = '0' + r;
  if (g.length === 1) g = '0' + g;
  if (b.length === 1) b = '0' + b;
  if (a.length === 1) a = '0' + a;

  const isDefaultOpacity = a.toLowerCase() === 'ff';
  return '#' + r + g + b + (withAlpha && !isDefaultOpacity ? a : '');
}

export function toHexFormat(color: string): string {
  if (color === 'transparent') {
    return FALLBACK_COLOR_BRIGHT_DEFAULT + '00';
  }
  if ((color.startsWith('rgb(') || color.startsWith('rgba(')) && color.endsWith(')')) {
    const rgba = color.replace(/^(rgba\()|^(rgb\()|(\s)|(\))$/g, '').split(',');
    if (rgba.length === 4) {
      return rgbaToHexA(rgba, true);
    } else if (rgba.length === 3) {
      return rgbaToHexA(rgba);
    } else throw Error('[ricos-common] themeUtils.ts: Bad RGB / RGBA value: ' + color);
  }
  if (!color.startsWith('#')) {
    throw Error(
      `[ricos-common] themeUtils.ts: Bad Hex (${color}).
      Ricos color can only accept "transparent" or a HEX formatted color as its value`
    );
  }
  return color;
}

export function canTransformToHex(color: string): boolean {
  if (color === 'transparent') {
    return true;
  }
  if ((color.startsWith('rgb(') || color.startsWith('rgba(')) && color.endsWith(')')) {
    const rgba = color.replace(/^(rgba\()|^(rgb\()|(\s)|(\))$/g, '').split(',');
    return rgba.length === 4 || rgba.length === 3;
  }
  if (!color.startsWith('#')) {
    return false;
  }
  return true;
}

/**
 * Brightness of a HEX color (`0-255`)
 * @param hexCode Color in HEX format
 */
function getBrightness(hexCode: string): number {
  const _hexCode = hexCode.replace('#', '');

  const r = parseInt(_hexCode.substr(0, 2), 16);
  const g = parseInt(_hexCode.substr(2, 2), 16);
  const b = parseInt(_hexCode.substr(4, 2), 16);

  return (r * 299 + g * 587 + b * 114) / 1000;
}

/**
 * Ricos Brightness standard
 * @param hexColor Color in HEX format
 */
export function isBright(hexColor?: string): boolean {
  if (!hexColor) {
    return false;
  }
  return getBrightness(hexColor) > 150;
}

/**
 * Converts a given action color into a default, darker `fallbackColor`
 * if the given one is too bright. else, returns `actionColor`.
 *
 * Use this to prevent "bright-on-bright" content occurrence.
 * @param actionColor HEX Format
 */
export function adaptForeground(actionColor?: string, fallbackColor?: string): string | undefined {
  if (!actionColor) {
    return undefined;
  }
  return getBrightness(actionColor) < 150 ? actionColor : fallbackColor || FALLBACK_COLOR_DEFAULT;
}

/**
 * Converts `hexColor` from HEX format to RGB format
 * @param hexColor color in HEX format
 * @returns `RGB` object
 */
function hexToRgbA(hexColor: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(
    hexColor.toLowerCase()
  );
  if (result) {
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
      a: parseInt(result[4], 16),
    };
  }
  // Fail silently since primarily we use RGB relative values
  // and this is only used as a fallback
  console.error('Bad Hex');
  return undefined;
}

/**
 * Creates an RGB tuple based on a given HEX color.
 * (Used for CSS-Vars tuples in `params.scss`).
 * @example
 * toRgbTuple('#FFFFFF') => '255, 255, 255'
 *
 * @param hexColor color in HEX format
 * @returns RGB tuple
 */
export function toRgbTuple(hexColor?: string) {
  if (!hexColor) {
    return undefined;
  }
  const rgb = hexToRgbA(hexColor);
  if (!rgb) {
    return undefined;
  }
  const { r, g, b } = rgb;
  return `${r}, ${g}, ${b}`;
}

/**
 * Converts `hexColor` from HEX format to a CSS RGBA string
 * @example
 * toCssRgbA('#FFFFFF', 0.5) => 'rgba(255, 255, 255, 0.5)'
 *
 * @param hexColor color in HEX format
 * @returns RGB object
 */
export function toCssRgbA(hexColor: string | undefined, opacity: number): string | undefined {
  if (!hexColor) {
    return hexColor;
  }
  if (/^#([A-Fa-f\d]{2}){1,4}$/.test(hexColor)) {
    const rgb = hexToRgbA(hexColor);
    if (!rgb) {
      return undefined;
    }
    const { r, g, b, a } = rgb;
    return `rgba(${r}, ${g}, ${b}, ${opacity * (a || 1)})`;
  }
  throw new Error('[ricos-common] themeUtils.ts: Bad Hex');
}

export function toCssRgb(rgbTuple: string) {
  return `rgb(${rgbTuple})`;
}

export const toDashedKey = (str: string) =>
  str.replace(/([A-Z])/g, (all, letter) => '-' + letter.toLowerCase());

const spacing = ' '.repeat(4);
export const toVarStrings = (varsObject: CssVarsObject) => {
  const convertToRicosKey = (key: string) => '--ricos-' + toDashedKey(key);
  const cleanFromSemicolons = (value: unknown) =>
    typeof value === 'string' ? value.split(';')[0] : value;
  return Object.entries(varsObject)
    .filter(entry => !!entry[1])
    .map(entry => convertToRicosKey(entry[0]) + ': ' + cleanFromSemicolons(entry[1]) + ';\n')
    .join(spacing);
};

export const buildCssVars = (parentClass: string, ...varObjects: CssVarsObject[]) => `
  ${parentClass ? `.${parentClass}` : '*'} {
    ${varObjects
      .map(toVarStrings)
      .join(spacing)
      .replace(/[\s\t]+$/, '')}
  }\n`;

export const buildCssVarsObj = (varObject: CssVarsObject): CssVarsObject => {
  const convertToRicosKey = (key: string) => '--ricos-' + toDashedKey(key);

  return Object.entries(varObject).reduce(
    (acc, [key, value]) => ({ ...acc, [convertToRicosKey(key)]: value }),
    {}
  );
};

export const anyCssColorToHex = (color: string, element: HTMLElement): string => {
  let resolvedColor = color;
  if (color.startsWith('var(')) {
    resolvedColor = resolveCSSVar(color, element);
  }

  if (isRgbaTuple(resolvedColor)) {
    return rgbaToHexA(resolvedColor.split(','), true);
  }

  if (isRgbTuple(resolvedColor)) {
    return rgbaToHexA(resolvedColor.split(','));
  }

  return toHexFormat(resolvedColor);
};

const resolveCSSVar = (value: string, element: HTMLElement) => {
  const maxDepth = 5;
  let resolved = value;
  let depth = 0;

  while (resolved.startsWith('var(') && depth++ < maxDepth) {
    const match = resolved.match(/var\((--[^,\s)]+)(?:,\s*([^)]+))?\)/);
    if (!match) break;
    const [, varName, fallback] = match;
    const varValue = getComputedStyle(element).getPropertyValue(varName).trim();
    resolved = varValue || fallback?.trim() || resolved;
  }

  return resolved;
};

const isRgbaTuple = (color: string): boolean => {
  return color.match(/^\d{1,3},\d{1,3},\d{1,3},\d{1,3}$/) !== null;
};

const isRgbTuple = (color: string): boolean => {
  return color.match(/^\d{1,3},\d{1,3},\d{1,3}$/) !== null;
};
